import React, { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useGlobalLoading } from "@/contexts/GlobalLoadingContext";
import { AnimatedLogo } from "./AnimatedLogo";
type LoadingState = "idle" | "waiting" | "revealing" | "breathing" | "holding" | "exiting";

export const GlobalLoadingScreen = () => {
  const { isLoading } = useGlobalLoading();
  const [status, setStatus] = useState<LoadingState>("idle");
  const timersRef = useRef<number[]>([]);

  const clearTimers = () => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
  };

  const addTimer = (callback: () => void, delay: number) => {
    const timer = window.setTimeout(callback, delay);
    timersRef.current.push(timer);
  };

  useEffect(() => {
    return () => clearTimers();
  }, []);

  useEffect(() => {
    if (isLoading) {
      if (status === "idle" || status === "exiting") {
        clearTimers();
        setStatus("revealing");
        addTimer(() => {
          setStatus(prev => prev === "revealing" ? "breathing" : prev);
        }, 600); // Reveal duration
      }
    } else {
      if (status === "revealing" || status === "breathing") {
        clearTimers();
        setStatus("holding");
        addTimer(() => {
          setStatus("exiting");
          addTimer(() => {
            setStatus("idle");
          }, 750); // Curtain exit duration
        }, 150); // Hold duration
      }
    }
  }, [isLoading, status]);

  if (status === "idle") return null;

  return (
    <AnimatePresence>
      {(status !== "idle") && (
        <motion.div
          key="global-loading-screen"
          className="fixed inset-0 z-[99999] flex flex-col items-center justify-center pointer-events-none bg-black overflow-hidden"
          // Fade Exit instead of Curtain
          initial={{ opacity: 1 }}
          animate={{ opacity: status === "exiting" ? 0 : 1 }}
          transition={{ 
            duration: 0.5, 
            ease: "easeInOut"
          }}
        >
          {/* Logo Container */}
          <motion.div
            className="relative w-[min(46vw,46vh,320px)] aspect-square flex items-center justify-center"
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{
              opacity: status === "exiting" ? 0 : 1,
              scale: status === "holding" || status === "exiting" ? 1 : 
                     status === "breathing" ? [1, 1.015, 1] : 1,
              filter: status === "breathing" ? [
                "drop-shadow(0 0 10px rgba(255,106,0,0.15))", 
                "drop-shadow(0 0 15px rgba(255,106,0,0.3))", 
                "drop-shadow(0 0 10px rgba(255,106,0,0.15))"
              ] : "drop-shadow(0 0 0px rgba(255,106,0,0))"
            }}
            transition={{
              opacity: {
                duration: status === "revealing" ? 0.6 : (status === "exiting" ? 0.4 : 0),
                ease: "easeOut"
              },
              scale: status === "breathing" ? {
                duration: 2.9,
                ease: "easeInOut",
                repeat: Infinity,
                repeatType: "loop"
              } : {
                duration: status === "revealing" ? 0.6 : 0.3,
                ease: status === "revealing" ? [0.2, 0.8, 0.2, 1] : "easeInOut"
              },
              filter: status === "breathing" ? {
                duration: 2.9,
                ease: "easeInOut",
                repeat: Infinity,
                repeatType: "loop"
              } : {
                duration: 0.3,
                ease: "easeOut"
              }
            }}
          >
            <AnimatedLogo className="w-full h-full" />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
